import toast from 'react-hot-toast'
import type { Comment } from '@/@types/comment'
import BubbleIcon from '@/assets/bubble.svg?react'
import CopyIcon from '@/assets/copy.svg?react'
import DeleteOutlineIcon from '@/assets/delete-outline.svg?react'
import ActionButton from '@/components/ActionButton'
import { BlurTextMode, FeaturableBlurTextSwitch } from '@/components/BlurTextSwitch'
import CommentActionBar from '@/components/Comment/CommentActionBar'
import type { MenuItemData } from '@/components/DropdownMenu/types'
import { showConfirm } from '@/components/Modal/utils'
import { useCurrentUser } from '@/utils/hooks/useUser'
import styles from './StoryCommentActions.module.css'

const COPY_COMMENT_ACTION: MenuItemData = {
	icon: <CopyIcon />,
	id: 'COPY_COMMENT',
	title: '复制',
}

const DELETE_COMMENT_ACTION: MenuItemData = {
	icon: <DeleteOutlineIcon />,
	id: 'DELETE_COMMENT',
	title: '删除',
	props: {
		className: styles.deleteMenuItem,
	},
}

export interface StoryCommentActionsProps {
	canReply: boolean
	data: Comment
	onDelete: (data: Comment) => Promise<Comment>
	onReply: (data: Comment) => void
}

export default function StoryCommentActions({
	canReply,
	data,
	onDelete,
	onReply,
}: StoryCommentActionsProps) {
	const user = useCurrentUser()
	const menu = [COPY_COMMENT_ACTION, ...(data.user.id === user.id ? [DELETE_COMMENT_ACTION] : [])]

	const handleMenuSelect = async (menuId: string) => {
		try {
			if (menuId === COPY_COMMENT_ACTION.id) {
				await navigator.clipboard.writeText(data.content)
				toast.success('复制成功')
				return
			}

			if (menuId === DELETE_COMMENT_ACTION.id) {
				try {
					await showConfirm(
						'确定删除吗？',
						<div className={styles.confirmText}>删除后无法恢复</div>
					)
				} catch {
					return
				}

				await onDelete(data)
			}
		} catch {
			if (menuId === COPY_COMMENT_ACTION.id) {
				toast.error('复制失败')
			} else {
				toast.error('删除失败，请稍后重试')
			}
		}
	}

	return (
		<CommentActionBar menu={menu} onMenuSelect={handleMenuSelect}>
			{canReply && (
				<ActionButton
					tooltip="回复"
					onClick={() => onReply(data)}
					icon={<BubbleIcon width="20px" height="20px" />}
				>
					<FeaturableBlurTextSwitch data={String(data.replyCount ?? 0)} mode={BlurTextMode.SMALL} />
				</ActionButton>
			)}
		</CommentActionBar>
	)
}
